import { FC, Fragment } from "react";
import {
  Platform,
  StyleSheet,
  type GestureResponderEvent,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { useSegments } from "expo-router";
import * as DropdownMenuPrimitive from "@rn-primitives/dropdown-menu";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "lucide-react-native";
import { FadeIn } from "react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "react-native-screens";

import { TextClassContext } from "@/context/TextClassContext";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { ChildrenOrI18nType } from "@/i18n";
import { trackElementClicked } from "@/services/mixpanel";
import { cn } from "@/utils/tailwind";

import { VnThemedBox } from "./VnBox";
import { VnIcon } from "./VnIcon";
import { VnNativeOnlyAnimatedView } from "./VnNativeOnlyAnimatedView";
import { VnText } from "./VnText/VnText";

export const VnDropdownMenu = DropdownMenuPrimitive.Root;

export const VnDropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

export const VnDropdownMenuGroup = DropdownMenuPrimitive.Group;

export const VnDropdownMenuPortal = DropdownMenuPrimitive.Portal;

export const VnDropdownMenuSub = DropdownMenuPrimitive.Sub;

export const VnDropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

type VnDropdownMenuSubTriggerPropsType =
  DropdownMenuPrimitive.SubTriggerProps & {
    children?: React.ReactNode;
    iconClassName?: string;
    inset?: boolean;
  };

export const VnDropdownMenuSubTrigger: FC<
  VnDropdownMenuSubTriggerPropsType
> = ({ className, inset, children, iconClassName, ...props }) => {
  const { open: isOpen } = DropdownMenuPrimitive.useSubContext();
  const { isWeb } = usePlatformOS();

  const icon = isWeb
    ? ChevronRightIcon
    : isOpen
      ? ChevronUpIcon
      : ChevronDownIcon;

  return (
    <TextClassContext.Provider
      value={cn(
        "group-active:text-accent-foreground select-none text-sm",
        isOpen && "text-accent-foreground",
      )}>
      <DropdownMenuPrimitive.SubTrigger
        className={cn(
          "active:bg-accent group flex flex-row items-center rounded-sm px-2 py-2 sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none [&_svg]:pointer-events-none",
          }),
          isOpen && "bg-accent",
          inset && "pl-8",
        )}
        {...props}>
        <>{children}</>
        <VnIcon
          as={icon}
          className={cn(
            "text-foreground ml-auto size-4 shrink-0",
            iconClassName,
          )}
        />
      </DropdownMenuPrimitive.SubTrigger>
    </TextClassContext.Provider>
  );
};

type VnDropdownMenuSubContentPropsType =
  DropdownMenuPrimitive.SubContentProps & {
    className?: string;
  };

export const VnDropdownMenuSubContent: FC<
  VnDropdownMenuSubContentPropsType
> = ({ className, ...props }) => {
  return (
    <VnNativeOnlyAnimatedView entering={FadeIn}>
      <DropdownMenuPrimitive.SubContent
        className={cn(
          "border-border bg-card overflow-hidden rounded-md border p-1 shadow-lg shadow-black/5",
          Platform.select({
            web: "origin-(--radix-context-menu-content-transform-origin) animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem]",
          }),
          className,
        )}
        {...props}
      />
    </VnNativeOnlyAnimatedView>
  );
};

const FullWindowOverlay =
  Platform.OS === "ios" ? RNFullWindowOverlay : Fragment;

type VnDropdownMenuContentPropsType = DropdownMenuPrimitive.ContentProps & {
  overlayStyle?: StyleProp<ViewStyle>;
  overlayClassName?: string;
  portalHost?: string;
};

export const VnDropdownMenuContent: FC<VnDropdownMenuContentPropsType> = ({
  className,
  overlayClassName,
  overlayStyle,
  portalHost,
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Portal hostName={portalHost}>
      <FullWindowOverlay>
        <DropdownMenuPrimitive.Overlay
          style={Platform.select({
            // Due to updates to some package/dependencies, this keeps alternating between these two
            // web: overlayStyle ?? undefined,
            web:
              overlayStyle && typeof overlayStyle !== "string"
                ? (overlayStyle as StyleProp<ViewStyle>)
                : undefined,
            native: overlayStyle
              ? StyleSheet.flatten([
                  StyleSheet.absoluteFill,
                  overlayStyle as typeof StyleSheet.absoluteFill,
                ])
              : StyleSheet.absoluteFill,
          })}
          className={overlayClassName}>
          <VnThemedBox>
            <VnNativeOnlyAnimatedView entering={FadeIn}>
              <TextClassContext.Provider value="text-popover-foreground">
                <DropdownMenuPrimitive.Content
                  className={cn(
                    "border-border bg-popover min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg shadow-black/5",
                    Platform.select({
                      web: cn(
                        "max-h-(--radix-context-menu-content-available-height) origin-(--radix-context-menu-content-transform-origin) animate-in fade-in-0 zoom-in-95 z-50 cursor-default",
                        props.side === "bottom" && "slide-in-from-top-2",
                        props.side === "top" && "slide-in-from-bottom-2",
                      ),
                    }),
                    className,
                  )}
                  {...props}
                />
              </TextClassContext.Provider>
            </VnNativeOnlyAnimatedView>
          </VnThemedBox>
        </DropdownMenuPrimitive.Overlay>
      </FullWindowOverlay>
    </DropdownMenuPrimitive.Portal>
  );
};

type VnDropdownMenuItemPropsType = DropdownMenuPrimitive.ItemProps &
  ChildrenOrI18nType & {
    className?: string;
    inset?: boolean;
    variant?: "default" | "destructive";
  };

export const VnDropdownMenuItem: FC<VnDropdownMenuItemPropsType> = ({
  className,
  inset,
  variant,
  children,
  i18nKey,
  i18nOptions,
  onPress,
  ...props
}) => {
  const segments = useSegments();

  const handlePress = (e: GestureResponderEvent) => {
    trackElementClicked({
      route: `/${segments.join("/")}`,
      elementTextKey: i18nKey,
      elementText: typeof children === "string" ? children : undefined,
      elementType: "menu_item",
    });
    onPress?.(e);
  };

  return (
    <TextClassContext.Provider
      value={cn(
        "text-popover-foreground group-active:text-popover-foreground select-none text-sm",
        variant === "destructive" &&
          "text-destructive group-active:text-destructive",
      )}>
      <DropdownMenuPrimitive.Item
        className={cn(
          "active:bg-accent group relative flex flex-row items-center gap-2 rounded-sm px-2 py-2 sm:py-1.5",
          Platform.select({
            web: cn(
              "focus:bg-accent focus:text-accent-foreground cursor-default outline-none data-[disabled]:pointer-events-none",
              variant === "destructive" &&
                "focus:bg-destructive/10 dark:focus:bg-destructive/20",
            ),
          }),
          variant === "destructive" &&
            "active:bg-destructive/10 dark:active:bg-destructive/20",
          props.disabled && "opacity-50",
          inset && "pl-8",
          className,
        )}
        onPress={handlePress}
        {...props}>
        {children ?? <VnText i18nKey={i18nKey} i18nOptions={i18nOptions} />}
      </DropdownMenuPrimitive.Item>
    </TextClassContext.Provider>
  );
};

type VnDropdownMenuLabelPropsType = DropdownMenuPrimitive.LabelProps &
  ChildrenOrI18nType & {
    className?: string;
    inset?: boolean;
  };

export const VnDropdownMenuLabel: FC<VnDropdownMenuLabelPropsType> = ({
  className,
  inset,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Label
      className={cn("px-2 py-2 sm:py-1.5", inset && "pl-8", className)}
      {...props}>
      {children ?? <VnText i18nKey={i18nKey} i18nOptions={i18nOptions} />}
    </DropdownMenuPrimitive.Label>
  );
};

type VnDropdownMenuSeparatorPropsType = DropdownMenuPrimitive.SeparatorProps;

export const VnDropdownMenuSeparator: FC<VnDropdownMenuSeparatorPropsType> = ({
  className,
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
};
